import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClassificationResultItem } from 'types/document.types';
import { ResultData } from 'types/chat.types';
import { TableCell } from 'components/atoms/TableCell';
import Icon from 'components/atoms/Icon';
import { exportClassificationPdf } from 'utils/exportClassificationPdf';
import conversationService from 'services/conversationService';
import useAuthStore from 'stores/authStore';

interface HistoryTableRowProps {
  item: ClassificationResultItem;
  onClick?: (item: ClassificationResultItem) => void;
}

/**
 * Pick the structured classification result for a given classification id from a
 * conversation's messages. Prefers an exact id match; otherwise falls back to
 * the most recent message carrying a usable HTS classification.
 */
const findResultForClassification = (
  messages: Array<Record<string, any>>,
  classificationId: string,
): ResultData | null => {
  let fallback: ResultData | null = null;
  for (const msg of messages) {
    const result = msg.result as ResultData | undefined;
    if (!result?.hts_classification?.primary_hts_code) continue;
    if (result.classification_id === classificationId) return result;
    fallback = result;
  }
  return fallback;
};

/**
 * Derive a human-friendly product name from the result's filename (matching the
 * chat card's logic) or fall back to the original query.
 */
const deriveProductName = (result: ResultData, query: string): string => {
  if (result.filename) {
    return result.filename.replace(/\.[^/.]+$/, '').replace(/_/g, ' ');
  }
  return query || 'Product Classification';
};

export const HistoryTableRow: React.FC<HistoryTableRowProps> = ({ item, onClick }) => {
  const navigate = useNavigate();
  const userId = useAuthStore((state) => state.getUserId());
  const [exporting, setExporting] = useState(false);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 0.8) return 'text-green-700 bg-green-50';
    if (score >= 0.5) return 'text-yellow-700 bg-yellow-50';
    return 'text-red-700 bg-red-50';
  };

  const handleThreadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.conversation_id) {
      navigate('/chat', { state: { conversationId: item.conversation_id } });
    }
  };

  /** Export the thin row data as a summary PDF (used when no thread exists). */
  const exportSummary = () => {
    exportClassificationPdf(
      {
        primary_hts_code: item.hts_code || '—',
        product_category: item.query,
        confidence_score: item.confidence_score ?? undefined,
        duty_rate: item.duty_rate ?? undefined,
      },
      {
        productName: item.query || 'Classification',
        classificationId: item.classification_id,
      },
    );
  };

  /**
   * Export the full classification report. Loads the linked conversation to
   * recover the complete result (rationale, alternatives, tariff determination)
   * and falls back to a summary PDF if the full data cannot be resolved.
   */
  const handleExport = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (exporting) return;

    if (!item.conversation_id || !userId) {
      exportSummary();
      return;
    }

    setExporting(true);
    try {
      const conversation = await conversationService.getConversation(item.conversation_id, userId);
      const result = findResultForClassification(conversation.messages, item.classification_id);

      if (!result?.hts_classification) {
        exportSummary();
        return;
      }

      exportClassificationPdf(
        result.hts_classification,
        {
          productName: deriveProductName(result, item.query),
          countryOfOrigin:
            result.extracted_data?.gsp?.country_of_origin?.value ||
            result.hts_classification.country_of_origin,
          classificationId: item.classification_id,
        },
        result.tariff_determination,
      );
    } catch (err) {
      console.error('[HistoryTableRow] Failed to export full report, falling back to summary:', err);
      exportSummary();
    } finally {
      setExporting(false);
    }
  };

  return (
    <tr
      className={`hover:bg-gray-50 ${onClick ? 'cursor-pointer' : ''}`}
      onClick={() => onClick?.(item)}
    >
      <TableCell className="max-w-[280px] truncate">
        {item.query}
      </TableCell>
      <TableCell>
        {item.hts_code || '—'}
      </TableCell>
      <TableCell align="center">
        {item.confidence_score != null ? (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConfidenceColor(item.confidence_score)}`}
          >
            {(item.confidence_score * 100).toFixed(0)}%
          </span>
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell>{item.duty_rate || '—'}</TableCell>
      <TableCell>{formatDate(item.created_at)}</TableCell>
      <TableCell align="center">
        <div className="inline-flex items-center gap-4">
          {item.conversation_id && (
            <button
              onClick={handleThreadClick}
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              View
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-60 disabled:cursor-wait"
            title="Export this result as a PDF"
          >
            {exporting ? (
              <span className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icon name="download" className="w-3.5 h-3.5" />
            )}
            PDF
          </button>
        </div>
      </TableCell>
    </tr>
  );
};
